import axios from "axios";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { Typography, Box } from "@material-ui/core";
import { Loader } from "../components/customComponents";
import TrainingWizard from "./TrainingWizard";
import { TrainingCertificate } from "./TrainingCertificate";
import RadialBar from "./ProgressBar/NewChart";
import Signature from "../assets/signature.png";
import Signature1 from "../assets/signature-nk.png";
import { GET_MODULE_PROGRESS, VERIFY_CERTIFICATE, SET_MODULE_STATUS } from "../constants/urlConstants";
import { checkAccessList } from "../common/utilities";
import { utilities } from "../components/utilities";

/* ---------- small helpers ---------- */
const isString = (v) => typeof v === "string";
const isHttpUrl = (v) => isString(v) && /^https?:\/\//i.test(v);
const safeFileName = (v) => (isString(v) ? v.trim() : null);

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      if (comma === -1) return reject(new Error("Invalid data URL"));
      resolve(result.slice(comma + 1));
    };
    reader.onerror = () => reject(new Error("Could not read the file"));
    reader.readAsDataURL(blob);
  });

/* -------------------- Axios helpers -------------------- */
const http = axios.create({ timeout: 20000, withCredentials: false });

async function submitToLambda({ certificate_requirements, employee, training, module, submission }) {
  const resp = await http.post(VERIFY_CERTIFICATE, { certificate_requirements, employee, training, module, submission });
  return resp?.data;
}

async function setModuleStatus({ employee_id, training_id, training_name, module_id, status }) {
  try {
    const resp = await http.post(SET_MODULE_STATUS, { employee_id, training_id, module_id, training_name, status });
    return resp?.data;
  } catch {
    return null;
  }
}

/* ---------- NEW: grouped helpers ---------- */
function normalizeModuleProgress(moduleData) {
  const grouped = new Map();
  (moduleData || []).forEach((m) => {
    if (!m?.module_id) return;
    if (!grouped.has(m.module_id)) {
      grouped.set(m.module_id, { ...m });
    } else {
      const existing = grouped.get(m.module_id);
      if (m.is_pass) existing.is_pass = true;
      grouped.set(m.module_id, existing);
    }
  });
  return Array.from(grouped.values());
}

async function setGroupedModuleStatus({ employee_id, training_id, training_name, module_id, status, modules }) {
  const relatedModules = (modules || []).filter((m) => m.module_id === module_id);
  await Promise.all(
    relatedModules.map((mod) =>
      setModuleStatus({ employee_id, training_id, training_name, module_id: mod.module_id, status })
    )
  );
}

/* -------------------- Helpers for link/PDF validation -------------------- */
function normalizeAxiosError(err) {
  if (err?.code === "ECONNABORTED") {
    return { message: "Request timed out. Please try again.", status: 0, data: null };
  }
  if (!err?.response) {
    return {
      message: (err?.message && String(err.message)) || "Network error. Please try again.",
      status: 0,
      data: null,
    };
  }
  const { status, data } = err.response;
  let message =
    (data && (data.error || data.message || data.detail || data.title)) ||
    `Request failed with status ${status}`;
  if (typeof message === "object") {
    try { message = JSON.stringify(message); } catch { message = `Request failed with status ${status}`; }
  }
  return { message, status, data };
}

/* ---------------- Subcomponents ---------------- */
const CertificationModal = ({
  open,
  onClose,
  onUpload,
  onSubmitLink,
  moduleName,
  submitting // NEW: controlled from parent
}) => {
  const [mode, setMode] = React.useState("upload");
  const [file, setFile] = React.useState(null);
  const [url, setUrl] = React.useState("");

  const canSubmit =
    (mode === "upload" && !!file) ||
    (mode === "link" && typeof url === "string" && /^https?:\/\//i.test(url.trim()));

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    if (mode === "upload" && file) await onUpload(file);
    if (mode === "link" && url) await onSubmitLink(url.trim());
    setMode("upload"); setFile(null); setUrl(""); onClose();
  };

  return (
    <Modal show={open} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Submit certificate – {moduleName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Typography variant="body1" gutterBottom>
          <b>Your name and certificate should be matched for verification. Make sure the certificate contains name as like in your academy profile.</b>
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Note: Uploaded files should be in PDF format or Links should be publicly accessible.
        </Typography>
        <hr />
        <Form>
          <div className="mb-3">
            <Form.Check inline type="radio" id="mode-upload" label="Upload PDF" name="submission-mode"
              value="upload" checked={mode === "upload"} onChange={(e) => setMode(e.target.value)} />
            <Form.Check inline type="radio" id="mode-link" label="Paste completion link" name="submission-mode"
              value="link" checked={mode === "link"} onChange={(e) => setMode(e.target.value)} />
          </div>
          {mode === "upload" ? (
            <Form.Group controlId="formFile">
              <Form.Label>Choose PDF certificate</Form.Label>
              <Form.Control type="file" accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
              {file && (<Form.Text className="text-success">Selected: {file.name}</Form.Text>)}
            </Form.Group>
          ) : (
            <Form.Group controlId="formLink">
              <Form.Label>Completion link</Form.Label>
              <Form.Control type="url" placeholder="https://example.com/certificate"
                value={url} onChange={(e) => setUrl(e.target.value)} />
            </Form.Group>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit || submitting}>
          {submitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Submitting…
            </>
          ) : (
            "Submit"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};


const TrainingHeader = ({ trainingData, trainingStatus }) => {
  const { training_name, trainer_name, training_description, training_highlights } = trainingData || {};
  return (
    <>
      <Typography variant="h5" gutterBottom>{training_name}</Typography>
      {trainer_name && (<Typography variant="subtitle1" color="textSecondary">Presenter: {trainer_name}</Typography>)}
      {training_description && (<Typography variant="body1" gutterBottom>{training_description}</Typography>)}
      {training_highlights && training_highlights.length > 0 && (
        <ul style={{ marginTop: "0px", marginBottom: "8px" }}>
          {training_highlights.map((point, index) => (<li key={index} style={{ padding: "0px" }}>{point}</li>))}
        </ul>
      )}
      {trainingStatus?.completion_date && (
        <span>
          <i>Last Taken: {trainingStatus.completion_date}</i><br />
          <i>Expiry Date: {trainingStatus.expiry_date}</i><br /><br />
        </span>
      )}
    </>
  );
};

const ModuleTable = ({
  trainingData,
  getModulePassed,
  handleWizard,
  fileUrlMap,
  sequential,
  user,
  requiresCertification,
  onProgressUpdated,
}) => {
  const [certModalOpen, setCertModalOpen] = React.useState(false);
  const [certModalMod, setCertModalMod] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState({});
  const [moduleProgress, setModuleProgress] = React.useState({}); // per-module progress

  const setSubmittingFor = React.useCallback((moduleId, val) => {
    setIsSubmitting((prev) => ({ ...prev, [moduleId]: !!val }));
  }, []);

  const downloadFile = React.useCallback((url) => {
    if (isString(url) && url.startsWith("http")) {
      const link = document.createElement("a");
      link.href = url;
      link.rel = "noopener noreferrer";
      link.download = "";
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  }, []);

  const renderPresentationLinks = React.useCallback(
    (mod, trainingData, user) => {
      const list = Array.isArray(mod?.document_links) ? mod.document_links : [];
      return list.map((dl, idx) => {
        const title = dl?.title;
        const linkValue = dl?.link;
        if (!isString(title) || title.trim() === "") return null;

        if (isHttpUrl(linkValue)) {
          return (
            <React.Fragment key={`${mod.module_id || mod.module_name}-ext-${idx}`}>
              <a
                href={linkValue}
                target="_blank"
                rel="noopener noreferrer"
                className="link-decor"
                onClick={() =>
                  setGroupedModuleStatus({
                    employee_id: user?.employee_id,
                    training_id: trainingData?.training_id,
                    training_name: trainingData?.training_name,
                    module_id: mod?.module_id,
                    status: "In Progress",
                    modules: trainingData?.modules,
                  })
                }
              >
                {title}
              </a>
              <br />
            </React.Fragment>
          );
        }

        const fileName = safeFileName(linkValue);
        if (fileName) {
          const downloadUrl = fileUrlMap.get(fileName);
          if (!downloadUrl) return null;
          return (
            <React.Fragment key={`${mod.module_id || mod.module_name}-int-${idx}`}>
              <p
                className="link-decor"
                role="button"
                tabIndex={0}
                style={{ margin: 0, cursor: "pointer" }}
                onClick={async () => {
                  await setGroupedModuleStatus({
                    employee_id: user?.employee_id,
                    training_id: trainingData?.training_id,
                    training_name: trainingData?.training_name,
                    module_id: mod?.module_id,
                    status: "In Progress",
                    modules: trainingData?.modules,
                  });
                  downloadFile(downloadUrl);
                }}
              >
                {title}
              </p>
              <br />
            </React.Fragment>
          );
        }
        return null;
      });
    },
    [fileUrlMap]
  );

  // Local helper to POST and keep HTTP status
  async function postCertificate(payload) {
    const resp = await http.post(VERIFY_CERTIFICATE, payload);
    return { status: resp?.status, data: resp?.data };
  }

  /* ---------- per-module Lambda submit using HTTP status ---------- */
  async function submitCertificate({ mod, submission }) {
    try {
      const requiredCount = mod?.items_to_submit || 1;

      const payload = {
        certificate_requirements: mod?.certificate_requirements || [],
        employee: {
          employee_id: user?.employee_id,
          employee_name: user?.employee_name,
        },
        training: {
          training_id: trainingData?.training_id,
          training_name: trainingData?.training_name,
          total_modules: new Set(trainingData?.modules?.map((m) => m.module_id)).size,
        },
        module: {
          module_id: mod?.module_id,
          certificate_id: mod?.certificate_id || null,
        },
        submission,
      };

      const res = await postCertificate(payload);
      // Response shape: { status, data: { statusCode, body } }
      const statusCode = res?.data?.statusCode ?? res?.status;
      let errorMsg = "❌ Submission failed";

      // Try to parse error details from body if present
      if (res?.data?.body) {
        try {
          const bodyObj = JSON.parse(res.data.body);
          if (bodyObj?.error === "verification_failed" && bodyObj?.verification?.details?.error) {
            errorMsg = `❌ Submission failed: ${bodyObj.verification.details.error}`;
            if (bodyObj.verification.details.match_reason) {
              errorMsg += ` (${bodyObj.verification.details.match_reason})`;
            }
          }
        } catch {
          // ignore parse errors
        }
      }

      // Success only if HTTP 2xx and statusCode 200
      if (statusCode < 200 || statusCode >= 300) {
        utilities.showToast(errorMsg);
        return;
      }

      // Track per-module submission progress
      setModuleProgress((prev) => {
        const newCount = (prev[mod.module_id] || 0) + 1;
        if (newCount >= requiredCount) {
          utilities.showToast(`✅ Module completed.`);
          onProgressUpdated?.();
        } else {
          utilities.showToast(
            `📥 Certificate uploaded (${newCount}/${requiredCount}) for this module.`
          );
        }
        return { ...prev, [mod.module_id]: newCount };
      });
    } catch (err) {
      const { message } = normalizeAxiosError(err);
      utilities.showToast(`❌ Submission failed: ${message}`, true, 10000);
    }
  }

  const openCertModal = (mod) => {
    setCertModalMod(mod);
    setCertModalOpen(true);
  };
  const closeCertModal = () => setCertModalOpen(false);

  if (!trainingData?.modules?.length)
    return <p>No training information available.</p>;

  // group IDs for sequential check (kept as-is)
  const uniqueModuleIds = [...new Set(trainingData.modules.map((m) => m.module_id))];

  return (
    <>
      <table className="table table-striped table-bordered table-hover">
        <thead>
          <tr>
            <th>Name</th>
            <th>Presentation</th>
            {requiresCertification && <th>Certification</th>}
          </tr>
        </thead>
        <tbody>
          {trainingData?.modules.map((mod, idx) => {
            const passed = getModulePassed(mod);

            // group-aware sequential lock
            const groupIndex = uniqueModuleIds.indexOf(mod.module_id);
            const isLocked =
              sequential &&
              groupIndex > 0 &&
              !getModulePassed({ module_id: uniqueModuleIds[groupIndex - 1] });

            const requiredCount = mod?.items_to_submit || 1;
            const currentCount = moduleProgress[mod.module_id] || 0;

            return (
              <tr key={`${mod.module_id}-${idx}`}>
                <td>
                  <span
                    className={`${
                      isLocked
                        ? "disabled"
                        : !mod.requires_certification
                        ? "link-decor"
                        : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    style={{
                      cursor:
                        isLocked || mod.requires_certification ? "not-allowed" : "pointer",
                      color: isLocked ? "gray" : "inherit",
                    }}
                    onClick={async () => {
                      if (isLocked || mod.requires_certification) return;
                      await setGroupedModuleStatus({
                        employee_id: user?.employee_id,
                        training_id: trainingData?.training_id,
                        training_name: trainingData?.training_name,
                        module_id: mod?.module_id,
                        status: "In Progress",
                        modules: trainingData?.modules,
                      });
                      handleWizard(mod.module_name);
                    }}
                  >
                    {mod.module_name}
                  </span>
                  {passed && (
                    <>
                      &nbsp;
                      <i className="fa fa-check-circle" style={{ color: "green" }} />
                    </>
                  )}
                  {isLocked && (
                    <>
                      &nbsp;
                      <i className="fa fa-lock" style={{ color: "red" }} />
                    </>
                  )}
                </td>
                <td>
                  {!isLocked ? (
                    renderPresentationLinks(mod, trainingData, user)
                  ) : (
                    <span style={{ color: "gray" }}>
                      🔒 Locked until previous module is completed
                    </span>
                  )}
                </td>
                {requiresCertification && (
                  <td>
                    {isSubmitting[mod.module_id] ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Spinner animation="border" size="sm" />
                        <span>Submitting…</span>
                      </div>
                    ) : passed ? (
                      <span style={{ color: "green" }}>✅ Completed</span>
                    ) : isLocked ? (
                      <span style={{ color: "gray" }}>🔒 Locked</span>
                    ) : mod.requires_certification ? (
                      mod.enable_user_upload ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <Button
                            variant="outline-primary"
                            onClick={() => openCertModal(mod)}
                            disabled={!!isSubmitting[mod.module_id]}
                          >
                            Submit certificate
                          </Button>
                          {requiredCount > 1 && (
                            <span style={{ fontSize: "0.9em", color: "gray" }}>
                              Requires {requiredCount} submissions ({currentCount}/{requiredCount} done)
                            </span>
                          )}
                        </div>
                      ) : mod.enable_user_upload === false && mod.approval_message ? (
                        <span
                          style={{ color: "orange" }}
                          dangerouslySetInnerHTML={{ __html: mod.approval_message }}
                        />
                      ) : mod.enable_user_upload === false ? (
                        <span style={{ color: "orange" }}>
                          ⚠️ Requires Trainer/Manager Approval
                        </span>
                      ) : null
                    ) : (
                      <span style={{ color: "gray" }}>Not Required</span>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      <CertificationModal
        open={certModalOpen}
        onClose={closeCertModal}
        moduleName={certModalMod?.module_name}
        submitting={isSubmitting[certModalMod?.module_id]}
        onUpload={async (file) => {
          if (!file) return;
          if (file.type !== "application/pdf") {
            utilities.showToast("Please upload a PDF file.");
            return;
          }
          setSubmittingFor(certModalMod?.module_id, true);
          try {
            const fileBase64 = await blobToBase64(file);
            await submitCertificate({
              mod: certModalMod,
              submission: { type: "file", fileName: file.name, fileBase64 },
            });
          } finally {
            setSubmittingFor(certModalMod?.module_id, false);
          }
        }}
        onSubmitLink={async (url) => {
          if (!certModalMod) return;
          setSubmittingFor(certModalMod?.module_id, true);
          try {
            await submitCertificate({
              mod: certModalMod,
              submission: { type: "link", link: url },
            });
          } finally {
            setSubmittingFor(certModalMod?.module_id, false);
          }
        }}
      />
    </>
  );
};

const GlossaryList = ({ fileLinks, internalDocFileNames }) => {
  const downloadFile = useCallback((url) => {
    if (!isString(url)) return;
    const link = document.createElement("a");
    link.href = url; link.rel = "noopener noreferrer"; link.download = "";
    document.body.appendChild(link); link.click(); link.remove();
  }, []);

  const glossaryFiles = useMemo(() => {
    return (fileLinks || []).filter((item) => {
      const name = item?.name; const url = item?.["@microsoft.graph.downloadUrl"];
      return isString(name) && isString(url) && !internalDocFileNames.has(name);
    });
  }, [fileLinks, internalDocFileNames]);

  if (!glossaryFiles.length) return null;

  return (
    <div>
      <h5>Glossaries :</h5>
      {glossaryFiles.map((item, idx) => (
        <span
          key={item.name}
          className="link-decor"
          role="button"
          tabIndex={0}
          onClick={() => downloadFile(item["@microsoft.graph.downloadUrl"])}
          onKeyDown={(e) => e.key === "Enter" && downloadFile(item["@microsoft.graph.downloadUrl"])}
        >
          {item.name}{idx !== glossaryFiles.length - 1 && ", "}
        </span>
      ))}
    </div>
  );
};

const CompletionPanel = ({ moduleProgress, user, trainingData, moduleData }) => {
  if (!trainingData) return null;
  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <RadialBar series={moduleProgress} color="#008000" label="Completion" size={180} />
      {moduleProgress === 100 && (
        <Box textAlign="center" mt={3} width="100%">
          <Typography component="p" style={{ color: "green" }} gutterBottom>
            🎉 Congratulations! You have completed this training.
          </Typography>
          <TrainingCertificate
            name={user?.employee_name}
            trainingName={trainingData.training_name}
            date={moduleData?.[0]?.completion_date}
            directorName={user?.geography === "India" ? "Mathesh Jayabal" : "Niyaz"}
            designation={user?.geography === "India" ? "Managing Director" : "Managing Partner"}
            signature={user?.geography === "India" ? Signature : Signature1}
          />
        </Box>
      )}
    </Box>
  );
};

const isTrainingVisible = (training, user) => {
  const matchAttr = (field) => {
    const values = training?.[field];
    if (!values || values.length === 0) return true;  // ✅ default allow if not defined
    if (!Array.isArray(values)) return true;
    return (
      values.includes("All") ||
      values.includes(user?.[field]) ||
      checkAccessList("all_trainings")
    );
  };
  return (
    matchAttr("geography") &&
    matchAttr("team") &&
    matchAttr("competency") &&
    matchAttr("band")
  );
};

/* ---------- Main Training Content Component ------------- */
const TrainingContent = ({ trainingData, trainingStatus, fileLinks, user, setRefresh, refresh }) => {
  const [moduleData, setModuleData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);

  const fileUrlMap = useMemo(() => {
    const map = new Map();
    (fileLinks || []).forEach((item) => {
      const url = item?.["@microsoft.graph.downloadUrl"];
      const name = safeFileName(item?.name);
      if (name && isString(url)) map.set(name, url);
    });
    return map;
  }, [fileLinks]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    const fetchProgress = async () => {
      if (!trainingData?.training_id || !user?.employee_id) return;
      setIsLoading(true);
      try {
        const payload = { employee_id: user.employee_id, training_id: trainingData.training_id };
        const res = await http.post(GET_MODULE_PROGRESS, payload, { cancelToken: source.token });
        const data = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.body) ? res.data.body : []);
        if (mounted) setModuleData(normalizeModuleProgress(data));
      } catch (err) {
        const { message } = normalizeAxiosError(err);
        console.error("GET_MODULE_PROGRESS error:", err);
        if (mounted) { setModuleData([]); utilities.showToast(`Could not load module progress: ${message}`); }
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    fetchProgress();
    return () => { mounted = false; source.cancel("Component unmounted"); };
  }, [trainingData?.training_id, user?.employee_id, refresh]);

  const refreshProgress = React.useCallback(async () => {
    if (!trainingData?.training_id || !user?.employee_id) return;
    const source = axios.CancelToken.source();
    setIsLoading(true);
    try {
      const payload = { employee_id: user.employee_id, training_id: trainingData.training_id };
      const res = await http.post(GET_MODULE_PROGRESS, payload, { cancelToken: source.token });
      const data = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.body) ? res.data.body : []);
      setModuleData(normalizeModuleProgress(data));
    } catch (err) {
      const { message } = normalizeAxiosError(err);
      console.error("GET_MODULE_PROGRESS error:", err);
      setModuleData([]); utilities.showToast(`Could not load module progress: ${message}`);
    } finally {
      setIsLoading(false); source.cancel("cleanup");
    }
  }, [trainingData?.training_id, user?.employee_id]);

  useEffect(() => { refreshProgress(); }, [refreshProgress]);

  const modules = trainingData?.modules ?? [];
  const moduleProgress = useMemo(() => {
    const uniqueIds = [...new Set((modules || []).map((m) => m.module_id))];
    const total = uniqueIds.length; if (!total) return 0;
    const passedIds = new Set(moduleData.filter((m) => m?.is_pass).map((m) => m.module_id));
    return Math.round((passedIds.size / total) * 100);
  }, [moduleData, modules]);

  const getModulePassed = useCallback(
    (mod) =>
      moduleData.some((x) => x?.module_id === mod?.module_id && x?.is_pass),
    [moduleData]
  );

  const internalDocFileNames = useMemo(() => {
    const used = new Set();
    modules.forEach((mod) => {
      const links = Array.isArray(mod?.document_links) ? mod.document_links : [];
      links.forEach((dl) => {
        const link = dl?.link;
        if (isString(link) && !isHttpUrl(link)) { used.add(link.trim()); }
      });
    });
    return used;
  }, [modules]);

  const handleWizard = useCallback(
    (module_name) => {
      const match = modules.find((m) => m?.module_name === module_name);
      if (match) {
        setSelectedModule({ ...match, training_id: trainingData.training_id });
        setShowWizard(true);
      }
    },
    [modules, trainingData?.training_id]
  );

  if (isLoading) return <Loader />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      
      {!isTrainingVisible(trainingData, user) && (
        <Box mb={2}>
          <p style={{ color: "red" }}>
            🚫 You do not have access to this training.
          </p>
        </Box>
      )}

      <TrainingWizard
        show={showWizard}
        videoLink={selectedModule}
        selectedPath={trainingData?.training_name}
        setShowWizard={setShowWizard}
        videoLinksTableData={modules}
        mandatory={!!trainingStatus}
        setRefresh={setRefresh}
        onCompleted={refreshProgress}
      />

      <Box display="flex" flexDirection="row" gap={4} alignItems="center">
        <Box flex={2}><TrainingHeader trainingData={trainingData} trainingStatus={trainingStatus} /></Box>
        <Box flex={1}><CompletionPanel moduleProgress={moduleProgress} user={user} trainingData={trainingData} moduleData={moduleData} /></Box>
      </Box>

      <ModuleTable
        trainingData={trainingData}
        getModulePassed={getModulePassed}
        handleWizard={handleWizard}
        fileUrlMap={fileUrlMap}
        sequential={!!trainingData?.sequential_modules}
        user={user}
        requiresCertification={!!trainingData?.requires_certification}
        onProgressUpdated={refreshProgress}
      />

      <GlossaryList fileLinks={fileLinks} internalDocFileNames={internalDocFileNames} />
    </div>
  );
};

export default TrainingContent;
